import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Share2 } from "lucide-react";
import api, { resolveImg } from "../lib/api";
import { toast } from "sonner";

export default function BlogDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    setLoading(true);
    api
      .get(`/blog/${slug}`)
      .then(async (r) => {
        setPost(r.data);
        // Load 3 other posts as related
        const all = await api.get("/blog");
        setRelated(all.data.filter((p) => p.slug !== slug).slice(0, 3));
      })
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, text: post.excerpt, url });
      } catch (e) { /* cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied!");
      } catch (e) {
        toast.error("Could not copy");
      }
    }
  };

  if (loading) {
    return <div className="max-w-3xl mx-auto px-5 py-20"><div className="h-96 bg-[var(--muted)] rounded-3xl animate-pulse" /></div>;
  }

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-32 text-center">
        <h2 className="font-serif text-4xl text-[var(--ink-forest)]">Article not found</h2>
        <Link to="/blog" className="mt-6 inline-block bg-[var(--terracotta)] text-white rounded-full px-6 py-3 text-sm">Back to blog</Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-detail-page">
      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-[var(--muted-fg)] hover:text-[var(--terracotta)] mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <p className="eyebrow mb-3">{post.category.replace(/-/g, " ")}</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[var(--ink-forest)] tracking-tight leading-tight">
          {post.title}
        </h1>
        <div className="mt-5 flex items-center gap-5 text-sm text-[var(--muted-fg)]">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />
            {new Date(post.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </span>
          <span>By {post.author}</span>
          <button onClick={share} className="ml-auto inline-flex items-center gap-1.5 hover:text-[var(--terracotta)]" data-testid="blog-share-btn">
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>

        <div className="mt-8 rounded-3xl overflow-hidden aspect-[16/10]">
          <img src={resolveImg(post.cover_image)} alt={post.title} className="w-full h-full object-cover" />
        </div>

        <div className="mt-10 prose-content">
          {post.content.split("\n\n").map((para, i) => (
            <p key={i} className="text-[var(--ink-forest)] text-lg leading-relaxed mb-5 font-light">
              {para}
            </p>
          ))}
        </div>

        {post.tags?.length > 0 && (
          <div className="mt-10 pt-6 border-t border-[var(--border)] flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span key={t} className="text-xs px-3 py-1 rounded-full bg-[var(--muted)] text-[var(--ink-forest)]">
                {t}
              </span>
            ))}
          </div>
        )}
      </article>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-20 mb-16">
          <h2 className="font-serif text-3xl text-[var(--ink-forest)] mb-8">Keep reading</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link key={r.id} to={`/blog/${r.slug}`} className="group block">
                <div className="aspect-[4/3] overflow-hidden rounded-2xl bg-[var(--muted)]">
                  <img src={resolveImg(r.cover_image)} alt={r.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <h3 className="mt-4 font-serif text-xl text-[var(--ink-forest)] group-hover:text-[var(--terracotta)] transition-colors leading-tight">
                  {r.title}
                </h3>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
